import { AppsService } from '@/common/apps/apps.service.js';
import { LOADING_STATES } from '@/constants';
import { getActiveApplicationsMap } from '@/data/session/session-globals';
import { isMobileApp } from '@/utils/apps';
import moment, { DATE_FORMAT } from '@/utils/moment-wrapper';
import { connectAggregationToObservable, createRejectedStatus, getRows } from '@pendo/agg-connect';
import get from 'lodash/get';
import uniqBy from 'lodash/uniqBy';
import { Observable, Subject } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

/**
 * Store-free, typed replacement for the funnel-facing parts of `./utils.js`.
 *
 * Functions here never import the Vuex store; anything the original versions read from the store
 * (the report cache, impersonation, integrated-mobile-app state) is passed in as an argument so the
 * caller (a component with store access) owns the read. See `ReportStore` and the `*Deps` options.
 */

type TimeUnit = 'minute' | 'day' | 'days';

export interface TimeSeries {
    type: string;
    start?: string | number;
    end?: string | number;
}

export interface ReportDefinition {
    type?: string;
    timeSeries: TimeSeries;
}

export interface Report {
    id: string;
    type?: string;
    kind?: string;
    lastSuccessRunAt?: number;
    currentRunningAggStart?: number;
    definition: ReportDefinition;
}

type JsonPrimitive = string | number | boolean | null;

export interface JsonObject {
    [key: string]: JsonValue;
}

export type JsonValue = JsonPrimitive | JsonValue[] | JsonObject;

export const DATE_CUTOFF = new Date('2018-01-01');

export interface ReportErrorOptions {
    message?: string;
    retriable?: boolean;
}

export class ReportError extends Error {
    retriable: boolean;

    constructor ({ message = 'We were unable to run your report.', retriable = true }: ReportErrorOptions = {}) {
        super(message);
        this.message = message;
        this.retriable = retriable;
    }
}

export function isOlderThanX (timestamp: number | undefined, count: number, granularity: TimeUnit): boolean {
    if (!timestamp) return false;

    return moment(timestamp).isBefore(moment().subtract(count, granularity), granularity);
}

export function timeSeriesIncludesToday (timeSeries: TimeSeries): boolean {
    if (timeSeries.type === 'Today') return true;
    if (timeSeries.type === 'Custom') {
        return moment().isBetween(timeSeries.start, timeSeries.end, 'day');
    }

    return false;
}

export function usesMovingTimeseries (timeSeries: TimeSeries): boolean {
    return timeSeries.type !== 'Custom';
}

export function getReportType (report: {
    id?: string;
    type?: string;
    kind?: string;
    definition?: { type?: string; timeSeries?: TimeSeries };
}): string | undefined {
    if (report.kind === 'Workflow') return 'Workflow';

    return report.type || report.definition?.type;
}

export function isFunnelReport (report: Report): boolean {
    return getReportType(report) === 'Funnel';
}

export function isPathReport (report: Report): boolean {
    return getReportType(report) === 'Path';
}

export interface StaleDeps {
    isImpersonating: boolean;
}

export function isStale (report: Report, { isImpersonating }: StaleDeps): boolean {
    // never auto-run when impersonating
    if (isImpersonating) return false;
    const {
        lastSuccessRunAt,
        currentRunningAggStart,
        definition: { timeSeries },
    } = report;

    // if there's an agg running, use it unless it has been running for over 60mins.
    if (currentRunningAggStart && !isOlderThanX(currentRunningAggStart, 60, 'minute')) {
        return false;
    }

    // if there's a cached report, use it unless it has expired or has a "moving" timeSeries (Last 7 Days, etc)
    if (lastSuccessRunAt) {
        // Funnels and paths are expensive, so reuse the cached report as long as it is not expired
        if (isFunnelReport(report) || isPathReport(report)) {
            return isOlderThanX(lastSuccessRunAt, 14, 'days');
        }

        return (
            isOlderThanX(lastSuccessRunAt, 14, 'days') ||
            (timeSeriesIncludesToday(timeSeries) && isOlderThanX(lastSuccessRunAt, 15, 'minute')) ||
            (usesMovingTimeseries(timeSeries) && isOlderThanX(lastSuccessRunAt, 0, 'day'))
        );
    }

    // no running or cached agg means we must rerun
    return true;
}

function isTimeSeriesStartBeforeCutoff (report: Report): boolean {
    const startDate = new Date(report.definition.timeSeries.start ?? NaN);

    return report.definition.timeSeries.type === 'Custom' && startDate < DATE_CUTOFF;
}

export interface ReportStore<TReport extends Report> {
    getReport: (reportId: string) => TReport | undefined;
    commitUpdatedReport: (report: TReport) => void;
    isImpersonating: () => boolean;
}

async function validateReport<TReport extends Report> (
    validate: (report: TReport) => void | Promise<void>,
    reportId: string,
    getReport: (reportId: string) => TReport | undefined,
): Promise<void> {
    const report = getReport(reportId);
    if (!report) {
        throw new ReportError({
            message: 'Cannot find the report.',
            retriable: false,
        });
    }

    if (isTimeSeriesStartBeforeCutoff(report)) {
        throw new ReportError({
            message:
                'Paths & Funnels no longer support date ranges earlier than Jan 1, 2018. Please update your date range.',
            retriable: false,
        });
    }

    await validate(report);
}

export interface BuildReportStreamConfig<TReport extends Report, TParams, TValue> {
    aggregation: object;
    validate: (report: TReport) => void | Promise<void>;
    buildAggParams: (report: TReport) => TParams;
    processResponse: (report: TReport, rows: JsonObject[]) => TValue;
}

interface RawAggResponse<TReport extends Report> {
    status: string;
    value?: { report: TReport; data: JsonValue };
    error?: Error;
    notification?: number;
}

export interface ReportStreamEmission<TValue> {
    status: string;
    value?: TValue;
    error?: ReportError | Error;
    notification?: number;
}

export type ReportStreamSubject = Subject<[string, boolean]>;

export function buildReportStream<TReport extends Report, TParams, TValue> (
    { aggregation, validate, buildAggParams, processResponse }: BuildReportStreamConfig<TReport, TParams, TValue>,
    { getReport, commitUpdatedReport, isImpersonating }: ReportStore<TReport>,
): [Observable<ReportStreamEmission<TValue>>, ReportStreamSubject] {
    const subject: ReportStreamSubject = new Subject();
    const response$ = connectAggregationToObservable(
        aggregation,
        subject.pipe(
            mergeMap(async ([reportId, forceRun]: [string, boolean]) => {
                try {
                    await validateReport(validate, reportId, getReport);
                } catch (error) {
                    return createRejectedStatus(error);
                }

                const report = getReport(reportId)!;

                return {
                    report,
                    forceRun: forceRun || isStale(report, { isImpersonating: isImpersonating() }),
                    impersonating: isImpersonating(),
                    ...buildAggParams(report),
                };
            }),
        ),
        undefined,
    ).pipe(
        mergeMap(async (response: RawAggResponse<TReport>) => {
            const { LOADING, NOTIFICATION, REJECTED, RESOLVED } = LOADING_STATES;
            switch (response.status) {
                case LOADING:
                    return response;
                case NOTIFICATION:
                    return response;
                case REJECTED:
                    return response.error instanceof ReportError
                        ? response
                        : {
                            ...response,
                            error: new ReportError(),
                        };
                case RESOLVED: {
                    const { report } = (response.value!);
                    const rows: JsonObject[] = getRows(response.value!.data);
                    commitUpdatedReport(report);

                    return {
                        status: response.status,
                        value: processResponse(report, rows),
                    };
                }
            }

            return undefined;
        }),
    );

    return [response$, subject];
}

export function lastNDaysTimeseries (numDays: number): TimeSeries {
    const start = moment()
        .subtract(numDays, 'day')
        .startOf('day')
        .format(DATE_FORMAT.iso)
        .valueOf();
    const end = moment()
        .subtract(1, 'days')
        .startOf('day')
        .format(DATE_FORMAT.iso)
        .valueOf();

    return { type: `Last ${numDays} Days`, start, end };
}

export function getDefaultTimeseries (reportType?: string): TimeSeries {
    switch (reportType) {
        case 'Workflow':
            return lastNDaysTimeseries(7);
        default:
            return lastNDaysTimeseries(30);
    }
}

export interface MobileFrameworkFlavour {
    type?: string;
    sdkVersion?: string;
    pluginVersion?: string;
}

export interface MobileApp {
    id: string | number;
    name?: string;
    platform?: string;
    framework?: string;
    mobileFrameworkFlavour?: MobileFrameworkFlavour;
}

export interface MobileTargetEntity {
    id?: string | number;
    appId?: string | number;
}

export interface MobileProperties {
    includes_mobile: boolean;
    mobile_app_name?: string;
    mobile_platform?: string;
    mobile_framework?: string;
    mobile_framework_type?: string;
    mobile_sdk_version?: string;
    mobile_plugin_version?: string;
}

export interface MobilePropertiesDeps {
    hasIntegratedMobileApps: boolean;
}

interface AppsServiceConstants {
    ALL_PLATFORM?: string;
    ALL?: string;
    MOBILE_PLATFORM?: string;
    ALL_AGG_ID?: string;
    MOBILE_AGG_ID?: string;
}

export function getMobileProperties (
    targetEntities: MobileTargetEntity[] = [],
    { hasIntegratedMobileApps }: MobilePropertiesDeps = { hasIntegratedMobileApps: false },
): MobileProperties {
    const { ALL_PLATFORM, ALL, MOBILE_PLATFORM, ALL_AGG_ID, MOBILE_AGG_ID } = AppsService as AppsServiceConstants;
    const ALL_PLATFORM_IDS: Array<string | number | undefined> = [ALL_PLATFORM, ALL, ALL_AGG_ID];
    const MOBILE_PLATFORMS_IDS: Array<string | number | undefined> = [MOBILE_PLATFORM, MOBILE_AGG_ID];
    const isAllMobileAppsSelected = MOBILE_PLATFORMS_IDS.includes(targetEntities[0]?.id);

    if (isAllMobileAppsSelected) {
        return { includes_mobile: true };
    }

    const isAllAppsSelected = ALL_PLATFORM_IDS.includes(targetEntities[0]?.id);

    if (isAllAppsSelected && hasIntegratedMobileApps) {
        return { includes_mobile: true };
    }

    const appMap: Record<string, MobileApp> = getActiveApplicationsMap();
    const selectedApps = targetEntities.map((entity) => {
        const appId = entity.appId || entity.id;

        return appMap[appId as string];
    });

    let selectedMobileApps = selectedApps.filter((app) => isMobileApp(app?.platform));
    selectedMobileApps = uniqBy(selectedMobileApps, 'id');
    if (selectedMobileApps.length === 1) {
        const selectedMobileApp = selectedMobileApps[0];

        return {
            mobile_app_name: selectedMobileApp.name,
            mobile_platform: selectedMobileApp.platform,
            mobile_framework: selectedMobileApp.framework,
            mobile_framework_type: get(selectedMobileApp, 'mobileFrameworkFlavour.type', undefined),
            mobile_sdk_version: get(selectedMobileApp, 'mobileFrameworkFlavour.sdkVersion', undefined),
            mobile_plugin_version: get(selectedMobileApp, 'mobileFrameworkFlavour.pluginVersion', undefined),
            includes_mobile: true,
        };
    }

    return { includes_mobile: selectedMobileApps.length > 1 };
}
